import {useEffect, useMemo, useState} from 'react';
import {Button, Input, InputGroup, toaster, Uploader} from 'rsuite';
import {TfiClipIcon} from '../../../../../models/Icons.model';
import {SharedApi} from '../../../../../services/Shared.api';
import NotificationFactory from '../../../../../shared/Notifications';
import './DeviceFirmware.scss';

type DeviceFirmwareProps = {
  deviceId: string;
};

const DeviceFirmware = ({deviceId}: DeviceFirmwareProps) => {
  const [firmwareVersion, setFirmwareVersion] = useState('');
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [loading, setLoading] = useState(false);

  const selectedFile = fileList[0];
  const selectedBlob = selectedFile?.blobFile as Blob | undefined;
  const selectedFileSize = useMemo(() => {
    if (!selectedBlob?.size) return '';
    return `${(selectedBlob.size / 1024 / 1024).toFixed(2)} МБ`;
  }, [selectedBlob]);

  useEffect(() => {
    let isActual = true;

    setFirmwareVersion('');
    SharedApi.getDeviceInfo(deviceId)
      .then((device) => {
        if (isActual) {
          setFirmwareVersion(device.firmwareVersion || '');
        }
      })
      .catch((error) => {
        console.error('Ошибка получения версии прошивки устройства:', error);
      });

    return () => {
      isActual = false;
    };
  }, [deviceId]);

  const removeFile = () => {
    setFileList([]);
    setUploaderKey((value) => value + 1);
  };

  const uploadFirmware = async () => {
    if (!selectedBlob) return;

    setLoading(true);
    try {
      await SharedApi.uploadDeviceFirmware(deviceId, selectedBlob);
      toaster.push(
        NotificationFactory.success('Прошивка успешно загружена'),
        {duration: 3000, placement: 'topEnd'}
      );
      removeFile();
    } catch (error) {
      console.error('Ошибка загрузки прошивки устройства:', error);
      toaster.push(
        NotificationFactory.error('Не удалось загрузить прошивку'),
        {duration: 3000, placement: 'topEnd'}
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fieldset-wrapper device-firmware">
      <div className="legend-title main-legend-title">Прошивка</div>
      <div className="device-firmware__row">
        <Uploader
          key={`device-firmware-${deviceId}-${uploaderKey}`}
          accept=".bin,.hex"
          fileList={fileList}
          fileListVisible={false}
          autoUpload={false}
          multiple={false}
          listType="picture-text"
          disabled={loading}
          onChange={(files) => setFileList(files.slice(-1))}
          action=""
        >
          <Button color="green" appearance="primary" disabled={loading}>
            Добавить файл
          </Button>
        </Uploader>

        {selectedFile && (
          <div className="device-firmware__selected-file">
            <TfiClipIcon className="device-firmware__selected-file-icon"/>
            <span className="device-firmware__selected-file-name" title={selectedFile.name}>
              {selectedFile.name || 'Файл выбран'}
            </span>
            {selectedFileSize && (
              <span className="device-firmware__selected-file-size">{selectedFileSize}</span>
            )}
            <button
              type="button"
              className="device-firmware__selected-file-remove"
              onClick={removeFile}
              disabled={loading}
              aria-label="Удалить выбранный файл"
            >
              ×
            </button>
          </div>
        )}

        {selectedFile && (
          <Button
            appearance="primary"
            color="green"
            loading={loading}
            disabled={loading || !selectedBlob}
            onClick={uploadFirmware}
          >
            Загрузить
          </Button>
        )}

        <InputGroup className="device-firmware__version">
          <InputGroup.Addon>Версия</InputGroup.Addon>
          <Input value={firmwareVersion} readOnly placeholder="—"/>
        </InputGroup>
      </div>
    </div>
  );
};

export default DeviceFirmware;
